import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";

const links = [
  { label: "Workshops", href: "#schedule", testId: "nav-schedule-link" },
  { label: "Gallery", href: "#gallery", testId: "nav-gallery-link" },
  { label: "Pottery", href: "#pottery", testId: "nav-pottery-link" },
  { label: "FAQ", href: "#faq", testId: "nav-faq-link" },
  { label: "Visit", href: "#visit", testId: "nav-contact-link" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";
  const dark = !onHome || scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = dark
    ? "text-sm font-semibold text-neutral-500 hover:text-black transition-colors duration-300"
    : "text-sm font-semibold text-white/80 hover:text-white transition-colors duration-300";

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        dark ? "bg-[#eae4da]/85 backdrop-blur-2xl border-b border-black/[0.06] shadow-[0_8px_30px_-18px_rgba(0,0,0,0.2)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <a href={onHome ? "#top" : "/"} data-testid="nav-logo" className="leading-none">
          <span className={`font-display font-extrabold tracking-tight text-lg transition-colors duration-300 ${dark ? "text-[#1d1d1b]" : "text-white"}`}>
            MAS Arts<span className="text-[#eac119]">.</span>
          </span>
          <span className={`block text-[9px] font-bold tracking-[0.28em] uppercase mt-1 transition-colors duration-300 ${dark ? "text-neutral-400" : "text-white/60"}`}>
            Sheboygan, WI
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.href}
              href={onHome ? l.href : `/${l.href}`}
              data-testid={l.testId}
              className={linkCls}
            >
              {l.label}
            </a>
          ))}
          <a
            href={onHome ? "#schedule" : "/#schedule"}
            data-testid="nav-book-now-button"
            className="btn-primary inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-full"
          >
            Book a Seat
            <ArrowRight size={15} />
          </a>
        </nav>

        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen(!open)}
          className={`md:hidden p-2 transition-colors ${dark ? "text-black" : "text-white"}`}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden bg-[#eae4da]/95 backdrop-blur-2xl border-b border-black/[0.06] overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={onHome ? l.href : `/${l.href}`}
                  data-testid={`mobile-${l.testId}`}
                  onClick={() => setOpen(false)}
                  className="text-lg font-display font-bold text-[#1d1d1b]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={onHome ? "#schedule" : "/#schedule"}
                data-testid="mobile-nav-book-now-button"
                onClick={() => setOpen(false)}
                className="btn-primary inline-flex items-center justify-center gap-2 font-bold px-5 py-3.5 rounded-full"
              >
                Book a Seat
                <ArrowRight size={16} />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
